import {
  buildDeepLinkRoute,
  classifyDeepLink,
  isMobileAppHost,
  resolveDynamicDeepLink,
} from '@/utils/deeplink';
import { openUrl } from '@/utils/browser-helper';

const APP_URL_BASE = 'https://mobile.heartofgodchurch.org';
const DEFAULT_EXTERNAL_LINK_METADATA = {
  source: 'church-updates-rich-text',
  page: 'ChurchUpdates',
};
const HTTP_PROTOCOLS = new Set(['http:', 'https:']);
const SHORT_LINK_HOSTS = new Set(['hogc.link', 'hogc.us', 'hogc.one']);
const HOME_ROUTE = { path: '/home', query: {}, hash: '' };

export function getAnchorFromEvent(event) {
  return event?.target?.closest?.('a') || null;
}

export function isShortLinkHost(hostname) {
  return typeof hostname === 'string' && SHORT_LINK_HOSTS.has(hostname.toLowerCase());
}

export function getChurchUpdateLinkAction(href) {
  if (typeof href !== 'string') {
    return { type: 'unsafe' };
  }

  const trimmedHref = href.trim();
  if (!trimmedHref || trimmedHref.startsWith('#')) {
    return { type: 'unsafe' };
  }

  if (trimmedHref.startsWith('/') && !trimmedHref.startsWith('//')) {
    return {
      type: 'relative',
      route: buildDeepLinkRoute(trimmedHref),
    };
  }

  const hasUrlScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmedHref);
  const isProtocolRelativeUrl = trimmedHref.startsWith('//');
  if (!hasUrlScheme && !isProtocolRelativeUrl) {
    return { type: 'unsafe' };
  }

  let url;
  try {
    url = new URL(trimmedHref, APP_URL_BASE);
  } catch {
    return { type: 'unsafe' };
  }

  if (!HTTP_PROTOCOLS.has(url.protocol)) {
    return { type: 'unsafe' };
  }

  if (isMobileAppHost(url.hostname) || isShortLinkHost(url.hostname)) {
    return {
      type: 'deep-link',
      url: url.href,
    };
  }

  return {
    type: 'external',
    url: url.href,
  };
}

async function routeDeepLink(router, url) {
  const action = await classifyDeepLink(url);
  if (action.type === 'direct' && action.route) {
    await router?.push?.(action.route);
    return true;
  }

  if (action.type === 'dynamic') {
    const route = await resolveDynamicDeepLink(action);
    if (route) {
      await router?.push?.(route);
      return true;
    }
  }

  if (action.type === 'home') {
    await router?.push?.(HOME_ROUTE);
    return true;
  }

  return false;
}

export async function handleChurchUpdateRichTextLinkClick(event, { router, metadata } = {}) {
  const anchor = getAnchorFromEvent(event);
  if (!anchor) {
    return false;
  }

  event.preventDefault?.();

  const linkAction = getChurchUpdateLinkAction(anchor.getAttribute('href') || '');
  if (linkAction.type === 'unsafe' || (linkAction.type === 'relative' && !linkAction.route)) {
    return true;
  }

  if (linkAction.type === 'relative') {
    await router?.push?.(linkAction.route);
    return true;
  }

  if (linkAction.type === 'deep-link') {
    const routed = await routeDeepLink(router, linkAction.url);
    if (routed) {
      return true;
    }
  }

  await openUrl(
    { url: linkAction.url },
    {
      ...DEFAULT_EXTERNAL_LINK_METADATA,
      title: anchor.textContent?.trim() || linkAction.url,
      ...metadata,
    },
  );
  return true;
}
